// This is a slice of state for the Participant Service.
import { createSlice } from '@reduxjs/toolkit';

import { getParticipantURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';
import { RADIO_BUTTON_NO } from '../components/ui/pickers/SelectorOptions';
import { getEmptyPaginationState } from '../utils/objectUtils';

import { apiCallBegan, apiCallDirectusBegan, apiVersion } from './api';

const currentParticipantId = localStorage.getItem('currentParticipantId');

const slice = createSlice({
    name: 'participantService',
    initialState: {
        participantActionPlans: [],
        participantAuditEventSearchData: [],
        alcoholConsumed: [],
        alcoholConsumedMetaData: {},
        appointmentSectionHeadings: {},
        bloodGlucoseEntries: [],
        bloodGlucoseEntriesMetaData: {},
        bloodPressureEntries: [],
        bloodPressureEntriesMetaData: {},
        bloodTests: [],
        bloodTestsMetaData: {},
        bmiEntries: [],
        bmiEntriesMetaData: {},
        bsopDetails: {},
        contractConstants: {},
        contractConstantsSearch: [],
        claimEvent: {},
        claimEvents: [],
        claimQueue: {},
        claimQueueForecast: [],
        claimQueues: [],
        claimQueuesMetaData: {},
        currentForm: {},
        currentFormParticipant: {},
        currentParticipant: {},
        currentParticipantJobReadiness: [],
        currentParticipantJobSearch: [],
        currentParticipantId: currentParticipantId ? currentParticipantId : '',
        currentQuestionnaire: {},
        darEntry: {},
        darEntries: [],
        darQueuesSearch: [],
        docmanTriggers: [],
        eligibilityGroup: {},
        eligibilityGroups: [],
        eligibilityGroupSearch: [],
        financialRequestsSearch: [],
        financialRequestsSearchMetaData: {},
        formContracts: [],
        forms: [],
        formsByContract: [],
        formsParticipant: [],
        healthIncidents: [],
        healthIncidentsMetaData: {},
        hmrcData: [],
        iwsRecordsByPlacementId: {},
        iwsRecordSearch: [],
        iwsRecordSearchMetaData: {},
        ltcpDetails: {},
        ndppData: {},
        occActivity: [],
        occProfiles: [],
        participantAssetRequests: [],
        participantEPCRatings: [],
        participantFinancialRequests: [],
        participantPrivacy: [],
        participants: [],
        participantsMetaData: {},
        participantsById: {},
        participantsByLoggedInUserServiceIds: [],
        participantsByLoggedInUserServiceIdsMetaData: getEmptyPaginationState(),
        recentParticipants: [],
        participantsSearchById: [],
        participantsSearchByIdMetaData: getEmptyPaginationState(),
        participantsSearch: [],
        participantsSearchMetaData: {},
        participantSearchConfig: { type: null, searchTerm: null },
        currentParticipantBarriers: [],
        currentParticipantCircumstances: {},
        currentParticipantConnectToWork: {},
        currentParticipantEmployability: {},
        currentParticipantGeneralInformation: {},
        currentParticipantHealthInformation: {},
        currentParticipantJobGoals: [],
        currentParticipantPastEducation: {},
        currentParticipantWorkConditionality: {},
        currentParticipantWorkHistory: {},
        participantSectionHeadings: {},
        participantTimelineEvent: {},
        participantTimelineEvents: [],
        participantTimelineEventsMetaData: {},
        participantTimelineEventsSearchData: [],
        participantTimelineEventsSearchMetaData: {},
        physicalActivities: [],
        physicalActivitiesMetaData: {},
        postcodeServiceTeamMapping: {},
        questionnaires: [],
        questionnaireAnswers: [],
        currentQuestionnaireAnswers: {},
        questionnaireContracts: {},
        serviceConstants: {},
        signPostings: [],
        signPostingsMetaData: {},
        smokingQuitAttempts: [],
        smokingQuitAttemptReviews: {},
        steps: [],
        stepsMetaData: {},
        t2drPersonalInformationData: {},
        t2drHealthDataEntries: [],
        t2drHealthDataEntriesMetaData: {},
        waistMeasurements: [],
        waistMeasurementsMetaData: {},
        loadedNdppData: false,
        loadingActionPlans: false,
        loadingAssetRequests: false,
        loadingBSOPDetails: false,
        loadingClaimQueuesForecast: false,
        loadingCurrentParticipant: false,
        loadingParticipantJobSearch: false,
        loadingClaimQueues: false,
        loadingClaimQueuesSearch: false,
        loadingDarQueuesSearch: false,
        loadingDocmanTriggers: false,
        loadingEPCRatings: false,
        loadingFinancialRequests: false,
        loadingFinancialRequestsSearch: false,
        loadingIwsRecords: false,
        loadingIwsRecordsByPlacementId: false,
        loadingLTCPDetails: false,
        loadingParticipantAuditEventSearchData: false,
        loadingParticipantBarriers: false,
        loadingParticipantConnectToWork: false,
        loadingParticipantJobGoals: false,
        loadingParticipantsSearch: false,
        loadingT2DRPersonalInformationData: false,
        loadingParticipantTimelineEvents: false,
        loadingParticipantTimelineEventsSearchData: false,
        loadingQuestionnaireAnswers: false,
        loadingQuestionnaireContracts: false,
        loadingQuestionnaires: false,
        participantsByLoggedInUserServiceIdsLoading: false,
        errorMessage: '',
        successMessage: '',
        businessLogicError: {},
        validationErrors: {},
        version: {}
    },
    reducers: {
        // These update the state. They can use mutable code and Immer is used
        // to convert this to  immutable code
        errorMessageSet: (state, action) => {
            state.errorMessage = action.payload;
            state.successMessage = '';
        },

        clearParticipantActionPlans: (state) => {
            state.participantActionPlans = [];
        },

        actionPlansLoading: (state) => {
            state.participantActionPlans = [];
            state.loadingActionPlans = true;
        },

        actionPlansLoadingFailure: (state) => {
            state.loadingActionPlans = false;
        },

        actionPlansLoaded: (state, action) => {
            state.participantActionPlans = action.payload;
            state.loadingActionPlans = false;
        },

        actionPlanCreated: (state, action) => {
            state.participantActionPlans = [...state.participantActionPlans, action.payload];
        },

        actionPlanUpdated: (state, action) => {
            state.participantActionPlans = state.participantActionPlans.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        alcoholConsumedLoading: (state) => {
            state.alcoholConsumed = [];
            state.alcoholConsumedMetaData = { loaded: false };
        },
        alcoholConsumedLoaded: (state, action) => {
            state.alcoholConsumed = action.payload;
            state.alcoholConsumedMetaData = { loaded: true };
        },
        clearAlcoholConsumed: (state) => {
            state.alcoholConsumed = [];
            state.alcoholConsumedMetaData = { loaded: false };
        },
        alcoholConsumedCreated: (state, action) => {
            state.alcoholConsumed = [...state.alcoholConsumed, action.payload];
        },
        alcoholConsumedUpdated: (state, action) => {
            state.alcoholConsumed = state.alcoholConsumed.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        alcoholConsumedDeleted: (state, action) => {
            state.alcoholConsumed = state.alcoholConsumed.filter(
                (el) => el.id !== action.payload.id
            );
        },

        appointmentSectionHeadingsLoaded: (state, action) => {
            state.appointmentSectionHeadings = action.payload;
        },
        clearAppointmentSectionHeadings: (state) => {
            state.appointmentSectionHeadings = {};
        },

        appointmentSectionHeadingsCreated: (state, action) => {
            state.appointmentSectionHeadings = action.payload;
        },

        appointmentSectionHeadingsUpdated: (state, action) => {
            state.appointmentSectionHeadings = action.payload;
        },

        clearParticipantAssetRequests: (state) => {
            state.participantAssetRequests = [];
        },

        assetRequestCreated: (state, action) => {
            state.participantAssetRequests = [...state.participantAssetRequests, action.payload];
        },

        assetRequestsLoading: (state) => {
            state.loadingAssetRequests = true;
        },

        assetRequestsLoadingFailure: (state) => {
            state.loadingAssetRequests = false;
        },

        assetRequestsLoaded: (state, action) => {
            state.loadingAssetRequests = false;
            state.participantAssetRequests = action.payload;
        },

        assetRequestsUpdated: (state, action) => {
            state.participantAssetRequests = state.participantAssetRequests.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        bloodGlucoseEntriesLoading: (state) => {
            state.bloodGlucoseEntriesMetaData = { loaded: false };
            state.bloodGlucoseEntries = [];
        },
        bloodGlucoseEntriesLoaded: (state, action) => {
            state.bloodGlucoseEntriesMetaData = { loaded: true };
            state.bloodGlucoseEntries = action.payload;
        },
        clearBloodGlucoseEntries: (state) => {
            state.bloodGlucoseEntriesMetaData = { loaded: false };
            state.bloodGlucoseEntries = [];
        },
        bloodGlucoseCreated: (state, action) => {
            state.bloodGlucoseEntries = [...state.bloodGlucoseEntries, action.payload];
        },
        bloodGlucoseUpdated: (state, action) => {
            state.bloodGlucoseEntries = state.bloodGlucoseEntries.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        bloodGlucoseDeleted: (state, action) => {
            state.bloodGlucoseEntries = state.bloodGlucoseEntries.filter(
                (el) => el.id !== action.payload.id
            );
        },

        bloodPressureEntriesLoading: (state) => {
            state.bloodPressureEntriesMetaData = { loaded: false };
            state.bloodPressureEntries = [];
        },
        bloodPressureEntriesLoaded: (state, action) => {
            state.bloodPressureEntriesMetaData = { loaded: true };
            state.bloodPressureEntries = action.payload;
        },
        clearBloodPressureEntries: (state) => {
            state.bloodPressureEntries = [];
            state.bloodPressureEntriesMetaData = { loaded: false };
        },
        bloodPressureCreated: (state, action) => {
            state.bloodPressureEntries = [...state.bloodPressureEntries, action.payload];
        },
        bloodPressureUpdated: (state, action) => {
            state.bloodPressureEntries = state.bloodPressureEntries.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        bloodPressureDeleted: (state, action) => {
            state.bloodPressureEntries = state.bloodPressureEntries.filter(
                (el) => el.id !== action.payload.id
            );
        },

        bloodTestsLoading: (state) => {
            state.bloodTestsMetaData = { loaded: false };
            state.bloodTests = [];
        },
        bloodTestsLoaded: (state, action) => {
            state.bloodTestsMetaData = { loaded: true };
            state.bloodTests = action.payload;
        },
        clearBloodTests: (state) => {
            state.bloodTests = [];
            state.bloodTestsMetaData = { loaded: false };
        },
        bloodTestCreated: (state, action) => {
            state.bloodTests = [...state.bloodTests, action.payload];
        },
        bloodTestUpdated: (state, action) => {
            state.bloodTests = state.bloodTests.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        bloodTestDeleted: (state, action) => {
            state.bloodTests = state.bloodTests.filter((el) => el.id !== action.payload.id);
        },

        bmiLoading: (state) => {
            state.bmiEntriesMetaData = { loaded: false };
            state.bmiEntries = [];
        },
        bmiLoaded: (state, action) => {
            state.bmiEntriesMetaData = { loaded: true };
            state.bmiEntries = action.payload;
        },
        clearBmiEntries: (state) => {
            state.bmiEntries = [];
            state.bmiEntriesMetaData = { loaded: false };
        },
        bmiCreated: (state, action) => {
            state.bmiEntries = [...state.bmiEntries, action.payload];
        },
        bmiUpdated: (state, action) => {
            state.bmiEntries = state.bmiEntries.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        bmiDeleted: (state, action) => {
            state.bmiEntries = state.bmiEntries.filter((el) => el.id !== action.payload.id);
        },

        bsopDetailsLoading: (state) => {
            state.loadingBSOPDetails = true;
        },
        bsopDetailsLoadingFailure: (state) => {
            state.loadingBSOPDetails = false;
        },
        bsopDetailsLoaded: (state, action) => {
            state.loadingBSOPDetails = false;
            state.bsopDetails = action.payload === '' ? {} : action.payload;
        },
        bsopDetailsUpdated: (state, action) => {
            state.bsopDetails = action.payload === '' ? {} : action.payload;
        },
        clearBSOPDetails: (state) => {
            state.loadingBSOPDetails = false;
            state.bsopDetails = {};
        },

        claimEventLoading: (state) => {
            state.claimEvent = {};
        },

        claimEventLoaded: (state, action) => {
            state.claimEvents = [
                ...state.claimEvents.filter((el) => el.id !== action.payload.id),
                action.payload
            ];
            state.claimEvent = action.payload;
        },

        claimEventSearchLoading: (state) => {
            state.claimEvent = {};
            state.claimEvents = [];
        },

        claimEventSearchLoaded: (state, action) => {
            state.claimEvents = action.payload;
            if (action.payload.length === 1) state.claimEvent = action.payload[0];
        },

        clearClaimEvents: (state) => {
            state.claimEvents = [];
            state.claimEvent = {};
        },

        claimQueueForecastLoaded: (state, action) => {
            state.loadingClaimQueuesForecast = false;
            state.claimQueueForecast = action.payload;
        },

        claimQueueForecastLoading: (state) => {
            state.loadingClaimQueuesForecast = true;
            state.claimQueueForecast = [];
        },

        clearCurrentParticipant: (state) => {
            state.currentParticipant = {};
        },

        clearCurrentParticipantPastEducation: (state) => {
            state.currentParticipantPastEducation = {};
        },

        claimQueuesSearchLoading: (state) => {
            state.loadingClaimQueuesSearch = true;
        },

        claimQueuesSearchLoaded: (state, action) => {
            state.loadingClaimQueuesSearch = false;
            const { content, ...rest } = action.payload;
            if (Array.isArray(content))
                state.claimQueues = rest.first
                    ? content.filter((el) => !el.suspended)
                    : [...state.claimQueues, ...content.filter((el) => !el.suspended)];
            state.claimQueuesMetaData = rest;
        },
        clearClaimQueues: (state) => {
            state.claimQueues = [];
            state.claimQueuesMetaData = {};
        },

        claimQueuesLoading: (state) => {
            state.loadingClaimQueues = true;
        },

        claimQueueUpdated: (state, action) => {
            state.loadingClaimQueues = false;
            state.claimQueues = state.claimQueues.map((el) =>
                el.id !== action.payload.id ? el : action.payload
            );
            state.claimQueue = action.payload;
        },

        claimQueuesUpdated: (state, action) => {
            state.loadingClaimQueues = false;
            state.claimQueues = state.claimQueues.map((el) =>
                el.id !== action.payload.id ? el : action.payload
            );
        },

        claimQueuesFailure: (state) => {
            state.loadingClaimQueues = false;
        },

        contractConstantsLoaded: (state, action) => {
            state.contractConstants = action.payload === '' ? {} : action.payload;
        },

        contractConstantsLoading: (state) => {
            state.contractConstants = {};
        },

        contractConstantsUpdated: (state, action) => {
            state.contractConstants = action.payload;
        },

        contractConstantsSearchLoaded: (state, action) => {
            state.contractConstantsSearch = action.payload;
        },

        clearContractConstantsSearch: (state) => {
            state.contractConstantsSearch = [];
        },

        darCreated: (state, action) => {
            state.darEntries = [...state.darEntries, action.payload];
            state.darEntry = action.payload;
        },

        darQueuesSearchLoaded: (state, action) => {
            state.loadingDarQueuesSearch = false;
            state.darQueuesSearch = action.payload;
        },

        darUpdated: (state, action) => {
            const darEntries = state.darEntries?.filter((el) => el.id !== action.payload.id);
            state.darEntries = [...darEntries, action.payload];
            state.darEntry = action.payload;
        },

        darQueuesSearchLoading: (state) => {
            state.loadingDarQueuesSearch = true;
            state.darQueuesSearch = [];
        },
        clearDarQueueSearch: (state) => {
            state.darQueuesSearch = [];
        },

        clearDarEntry: (state) => {
            state.darEntry = {};
        },
        clearDarQueue: (state) => {
            state.darEntries = [];
        },

        docmanTriggerCreated: (state, action) => {
            state.docmanTriggers = [...state.docmanTriggers, action.payload];
        },

        docmanTriggersLoading: (state) => {
            state.loadingDocmanTriggers = true;
            state.docmanTriggers = [];
        },

        docmanTriggersLoaded: (state, action) => {
            state.loadingDocmanTriggers = false;
            state.docmanTriggers = action.payload;
        },

        docmanTriggersLoadingFailure: (state, action) => {
            state.loadingDocmanTriggers = false;
            state.errorMessage = action.payload;
        },

        docmanTriggerUpdated: (state, action) => {
            state.docmanTriggers = state.docmanTriggers.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        eligibilityGroupCreated: (state, action) => {
            state.eligibilityGroups = [...state.eligibilityGroups, action.payload];
        },

        eligibilityGroupLoaded: (state, action) => {
            state.eligibilityGroups = state.eligibilityGroups.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.eligibilityGroup = action.payload;
        },

        eligibilityGroupsLoaded: (state, action) => {
            state.eligibilityGroups = action.payload.content;
        },

        eligibilityGroupSearchLoading: (state) => {
            state.eligibilityGroupSearch = [];
        },

        eligibilityGroupSearchLoaded: (state, action) => {
            state.eligibilityGroupSearch = action.payload;
        },
        clearEligibilityGroupSearch: (state) => {
            state.eligibilityGroupSearch = [];
        },

        eligibilityGroupUpdated: (state, action) => {
            state.eligibilityGroups = state.eligibilityGroups.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        eligibilityGroupDeleted: (state, action) => {
            state.eligibilityGroups = state.eligibilityGroups.filter(
                (el) => el.id !== action.payload
            );
        },

        epcRatingsLoading: (state) => {
            state.loadingEPCRatings = true;
        },
        epcRatingsLoaded: (state, action) => {
            state.loadingEPCRatings = false;
            state.participantEPCRatings = action.payload;
        },
        clearEPCRatings: (state) => {
            state.participantEPCRatings = [];
        },
        epcRatingsLoadingFailure: (state) => {
            state.loadingEPCRatings = false;
        },
        epcRatingCreated: (state, action) => {
            state.participantEPCRatings = [...state.participantEPCRatings, action.payload];
        },
        epcRatingUpdated: (state, action) => {
            state.participantEPCRatings = state.participantEPCRatings.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        financialRequestsSearchLoading: (state) => {
            state.loadingFinancialRequestsSearch = true;
        },

        financialRequestsSearchLoaded: (state, action) => {
            state.loadingFinancialRequestsSearch = false;
            const { content, ...rest } = action.payload;
            state.financialRequestsSearch = rest.first
                ? content
                : [...state.financialRequestsSearch, ...content];
            state.financialRequestsSearchMetaData = rest;
        },

        clearFinancialRequestsSearch: (state) => {
            state.financialRequestsSearch = [];
            state.financialRequestsSearchMetaData = {};
        },

        financialRequestCreated: (state, action) => {
            state.participantFinancialRequests = [
                ...state.participantFinancialRequests,
                action.payload
            ];
        },

        clearParticipantFinancialRequests: (state) => {
            state.participantFinancialRequests = [];
        },

        financialRequestsLoading: (state) => {
            state.loadingFinancialRequests = true;
        },

        financialRequestsLoadingFailure: (state) => {
            state.loadingFinancialRequests = false;
        },

        financialRequestsLoaded: (state, action) => {
            state.loadingFinancialRequests = false;
            state.participantFinancialRequests = action.payload;
        },

        financialRequestUpdated: (state, action) => {
            state.participantFinancialRequests = state.participantFinancialRequests.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        formContractsLoaded: (state, action) => {
            state.formContracts = action.payload;
        },
        clearFormContracts: (state) => {
            state.formContracts = [];
        },

        formContractUpdated: (state, action) => {
            state.formContracts = action.payload;
        },

        formCreated: (state, action) => {
            state.currentForm = action.payload;
            state.forms = [...state.forms, action.payload];
        },

        formLoading: (state) => {
            state.currentForm = {};
        },

        formLoaded: (state, action) => {
            state.currentForm = action.payload;
        },

        formUpdated: (state, action) => {
            const forms = state.forms.filter((el) => el.id !== action.payload.id);
            state.forms = [...forms, action.payload];
            state.currentForm = action.payload;
        },

        formsLoading: (state) => {
            state.forms = [];
        },

        formsLoaded: (state, action) => {
            state.forms = action.payload;
        },

        formsByContractLoaded: (state, action) => {
            state.formsByContract = action.payload;
        },

        setCurrentForm: (state, action) => {
            state.currentForm = action.payload;
        },

        formParticipantCreated: (state, action) => {
            state.currentFormParticipant = action.payload;
            state.formsParticipant = [...state.formsParticipant, action.payload];
        },

        formParticipantLoaded: (state, action) => {
            state.currentFormParticipant = action.payload;
        },

        formParticipantUpdated: (state, action) => {
            const formsParticipant = state.formsParticipant.filter(
                (el) => el.id !== action.payload.id
            );
            state.formsParticipant = [...formsParticipant, action.payload];
            state.currentFormParticipant = action.payload;
        },

        formsParticipantLoaded: (state, action) => {
            state.formsParticipant = action.payload;
        },
        clearFormsParticipant: (state) => {
            state.formsParticipant = [];
        },

        healthIncidentsLoading: (state) => {
            state.healthIncidentsMetaData = { loaded: false };
            state.healthIncidents = [];
        },
        healthIncidentsLoaded: (state, action) => {
            state.healthIncidentsMetaData = { loaded: true };
            state.healthIncidents = action.payload;
        },
        clearHealthIncidents: (state) => {
            state.healthIncidents = [];
            state.healthIncidentsMetaData = { loaded: false };
        },
        healthIncidentCreated: (state, action) => {
            state.healthIncidents = [...state.healthIncidents, action.payload];
        },
        healthIncidentUpdated: (state, action) => {
            state.healthIncidents = state.healthIncidents.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        healthIncidentDeleted: (state, action) => {
            state.healthIncidents = state.healthIncidents.map((el) =>
                el.id === action.payload.id ? { ...el, inactive: true } : el
            );
        },

        hmrcDataLoading: (state) => {
            state.hmrcData = [];
        },
        hmrcDataLoaded: (state, action) => {
            state.hmrcData = action.payload;
        },

        iwsRecordsByPlacementIdLoading: (state, action) => {
            state.loadingIwsRecordsByPlacementId = true;
            const { placementIds } = action.payload;
            placementIds.forEach((placementId) => {
                if (!state.iwsRecordsByPlacementId[placementId]) {
                    state.iwsRecordsByPlacementId[placementId] = {
                        iwsRecords: [],
                        metaData: {}
                    };
                }
            });
        },

        iwsRecordsByPlacementIdLoaded: (state, action) => {
            state.loadingIwsRecordsByPlacementId = false;
            const { content, ...rest } = action.payload;
            if (content?.length > 0) {
                const placementId = content[0].placementId;
                state.iwsRecordsByPlacementId[placementId] = {
                    iwsRecords: rest.first
                        ? content
                        : [...state.iwsRecordsByPlacementId[placementId].iwsRecords, ...content],
                    metaData: rest
                };
            }
        },

        clearIwsRecordsSearch: (state) => {
            state.iwsRecordSearch = [];
            state.iwsRecordSearchMetaData = {};
        },

        iwsRecordsLoading: (state) => {
            state.loadingIwsRecords = true;
        },

        iwsSearchRecordsLoaded: (state, action) => {
            state.loadingIwsRecords = false;
            const { content, ...rest } = action.payload;
            state.iwsRecordSearch = rest.first ? content : [...state.iwsRecordSearch, ...content];
            state.iwsRecordSearchMetaData = rest;
        },

        ndppDataLoaded: (state, action) => {
            state.loadedNdppData = true;
            state.ndppData = action.payload === '' ? {} : action.payload;
        },
        ndppDataCreated: (state, action) => {
            state.ndppData = action.payload === '' ? {} : action.payload;
        },
        ndppDataUpdated: (state, action) => {
            state.ndppData = action.payload === '' ? {} : action.payload;
        },
        clearNdppData: (state) => {
            state.loadedNdppData = false;
            state.ndppData = {};
        },

        ltcpDetailsLoading: (state) => {
            state.loadingLTCPDetails = true;
        },
        ltcpDetailsLoadingFailure: (state) => {
            state.loadingLTCPDetails = false;
        },
        ltcpDetailsLoaded: (state, action) => {
            state.loadingLTCPDetails = false;
            state.ltcpDetails = action.payload === '' ? {} : action.payload;
        },
        clearLTCPDetails: (state) => {
            state.loadingLTCPDetails = false;
            state.ltcpDetails = {};
        },
        ltcpDetailsCreated: (state, action) => {
            state.ltcpDetails = action.payload === '' ? {} : action.payload;
        },
        ltcpDetailsUpdated: (state, action) => {
            state.ltcpDetails = action.payload === '' ? {} : action.payload;
        },

        occActivityLoaded: (state, action) => {
            state.occActivity = action.payload;
        },
        clearOccActivity: (state) => {
            state.occActivity = [];
        },
        occProfilesLoaded: (state, action) => {
            state.occProfiles = action.payload;
        },
        clearOccProfiles: (state) => {
            state.occProfiles = [];
        },
        occProfileCreated: (state, action) => {
            state.occProfiles = [...state.occProfiles, ...action.payload];
        },

        participantConnectToWorkLoaded: (state, action) => {
            state.loadingParticipantConnectToWork = false;
            state.currentParticipantConnectToWork = action.payload === '' ? {} : action.payload;
        },

        participantConnectToWorkLoading: (state) => {
            state.loadingParticipantConnectToWork = true;
        },

        participantConnectToWorkLoadingFailure: (state) => {
            state.loadingParticipantConnectToWork = false;
        },

        participantConnectToWorkUpdated: (state, action) => {
            state.currentParticipantConnectToWork = action.payload;
        },

        clearCurrentParticipantConnectToWork: (state) => {
            state.currentParticipantConnectToWork = {};
        },

        participantGeneralInformationLoaded: (state, action) => {
            state.currentParticipantGeneralInformation =
                action.payload === '' ? { hasOwnTransport: RADIO_BUTTON_NO } : action.payload;
        },
        participantGeneralInformationUpdated: (state, action) => {
            state.currentParticipantGeneralInformation = action.payload;
        },
        clearParticipantGeneralInformation: (state) => {
            state.currentParticipantGeneralInformation = { hasOwnTransport: RADIO_BUTTON_NO };
        },

        participantHealthInformationLoaded: (state, action) => {
            state.currentParticipantHealthInformation = action.payload || {};
        },
        clearParticipantHealthInformation: (state) => {
            state.currentParticipantHealthInformation = {};
        },

        participantCircumstancesLoaded: (state, action) => {
            state.currentParticipantCircumstances = action.payload === '' ? {} : action.payload;
        },
        clearCurrentParticipantCircumstances: (state) => {
            state.currentParticipantCircumstances = {};
        },

        participantPastEducationLoaded: (state, action) => {
            state.currentParticipantPastEducation = action.payload === '' ? {} : action.payload;
        },

        clearParticipantJobGoals: (state) => {
            state.currentParticipantJobGoals = [];
        },

        participantJobGoalCreated: (state, action) => {
            state.currentParticipantJobGoals = [
                ...state.currentParticipantJobGoals,
                action.payload
            ];
        },

        participantJobGoalDeleted: (state, action) => {
            state.currentParticipantJobGoals = state.currentParticipantJobGoals.filter(
                (el) => el.id !== action.payload
            );
        },

        participantJobGoalUpdated: (state, action) => {
            const existing = state.currentParticipantJobGoals.find(
                (el) => el.id === action.payload.id
            );
            state.currentParticipantJobGoals = existing
                ? state.currentParticipantJobGoals.map((el) =>
                      el.id === action.payload.id ? action.payload : el
                  )
                : [...state.currentParticipantJobGoals, action.payload];
        },

        participantJobGoalsLoading: (state) => {
            state.loadingParticipantJobGoals = true;
        },

        participantJobGoalsLoadingFailure: (state) => {
            state.loadingParticipantJobGoals = false;
        },

        participantJobGoalsLoaded: (state, action) => {
            state.loadingParticipantJobGoals = false;
            state.currentParticipantJobGoals = action.payload === '' ? [] : action.payload;
        },

        participantJobReadinessLoading: (state) => {
            state.loadingParticipantJobReadiness = true;
        },
        participantJobReadinessLoaded: (state, action) => {
            state.loadingParticipantJobReadiness = false;
            state.currentParticipantJobReadiness = action.payload === '' ? [] : action.payload;
        },
        clearParticipantJobReadiness: (state) => {
            state.currentParticipantJobReadiness = [];
        },
        participantJobReadinessCreated: (state, action) => {
            state.currentParticipantJobReadiness = [
                ...state.currentParticipantJobReadiness,
                action.payload
            ];
        },
        participantJobReadinessUpdated: (state, action) => {
            const index = state.currentParticipantJobReadiness.findIndex(
                (el) => el.id === action.payload.id
            );
            if (index >= 0) state.currentParticipantJobReadiness[index] = action.payload;
            else
                state.currentParticipantJobReadiness = [
                    ...state.currentParticipantJobReadiness,
                    action.payload
                ];
        },

        participantJobSearchLoaded: (state, action) => {
            state.loadingParticipantJobSearch = false;
            state.currentParticipantJobSearch = action.payload === '' ? [] : action.payload;
        },

        participantJobSearchLoading: (state) => {
            state.loadingParticipantJobSearch = true;
        },

        participantJobSearchCreated: (state, action) => {
            state.currentParticipantJobSearch = [
                ...state.currentParticipantJobSearch,
                action.payload
            ];
        },

        participantJobSearchUpdated: (state, action) => {
            const index = state.currentParticipantJobSearch.findIndex(
                (el) => el.id === action.payload.id
            );
            if (index >= 0) state.currentParticipantJobSearch[index] = action.payload;
            else
                state.currentParticipantJobSearch = [
                    ...state.currentParticipantJobSearch,
                    action.payload
                ];
        },

        participantJobSearchDeleted: (state, action) => {
            state.currentParticipantJobSearch = state.currentParticipantJobSearch.filter(
                (el) => el.id !== action.payload.id
            );
        },

        participantPrivacyLoaded: (state, action) => {
            state.participantPrivacy = action.payload.entries;
        },

        participantEmployabilityLoading: (state) => {
            state.currentParticipantEmployability = {};
        },

        participantEmployabilityLoaded: (state, action) => {
            state.currentParticipantEmployability = action.payload === '' ? {} : action.payload;
            if (action.payload && 'jobGoals' in action.payload)
                state.currentParticipantJobGoals = action.payload.jobGoals;
        },
        clearParticipantEmployability: (state) => {
            state.currentParticipantEmployability = {};
        },

        participantCreated: (state, action) => {
            state.currentParticipant = action.payload;
            state.participants = [...state.participants, action.payload];
        },

        participantUpdated: (state, action) => {
            state.currentParticipant = action.payload;
        },

        participantLoading: (state) => {
            state.loadingCurrentParticipant = true;
            state.currentParticipant = {};
        },

        participantLoaded: (state, action) => {
            state.loadingCurrentParticipant = false;
            state.currentParticipant = action.payload;
        },

        participantsLoaded: (state, action) => {
            const { content, ...rest } = action.payload;
            state.participants = rest.first ? content : [...state.participants, ...content];
            state.participantsMetaData = rest;
            content.forEach((el) => (state.participantsById[el.id] = el));
        },

        participantsByLoggedInUserServiceIdsLoading: (state) => {
            state.participantsByLoggedInUserServiceIdsLoading = true;
        },

        participantsByLoggedInUserServiceIdsLoaded: (state, action) => {
            state.participantsByLoggedInUserServiceIdsLoading = false;
            const { content, ...rest } = action.payload;
            state.participantsByLoggedInUserServiceIds = rest.first
                ? content
                : [...state.participantsByLoggedInUserServiceIds, ...content];
            state.participantsByLoggedInUserServiceIdsMetaData = rest;
            content.forEach((el) => (state.participantsById[el.id] = el));
        },

        clearParticipantsSearch: (state) => {
            state.participantsSearch = [];
            state.participantsSearchMetaData = {};
        },

        participantsSearchLoading: (state) => {
            state.loadingParticipantsSearch = true;
        },

        participantsSearchLoaded: (state, action) => {
            state.loadingParticipantsSearch = false;
            const { content, ...rest } = action.payload;
            state.participantsSearch = rest.first
                ? content
                : [...state.participantsSearch, ...content];
            state.participantsSearchMetaData = rest;
            for (const el of content) {
                state.participantsById[el.id] = el;
            }
        },

        participantsSearchByIdLoaded: (state, action) => {
            const { content, ...rest } = action.payload;
            state.participantsSearchById = rest.first
                ? content
                : [...state.participantsSearchById, ...content];
            state.participantsSearchByIdMetaData = rest;
        },

        recentParticipantsLoaded: (state, action) => {
            state.recentParticipants = action.payload;
        },

        participantBarriersLoading: (state) => {
            state.loadingParticipantBarriers = true;
        },
        participantBarriersLoadingFailure: (state) => {
            state.loadingParticipantBarriers = false;
        },
        participantBarriersLoaded: (state, action) => {
            state.loadingParticipantBarriers = false;
            state.currentParticipantBarriers = action.payload;
        },
        clearParticipantBarriers: (state) => {
            state.currentParticipantBarriers = [];
        },
        participantBarrierAdded: (state, action) => {
            state.currentParticipantBarriers = [
                ...state.currentParticipantBarriers,
                action.payload
            ];
        },
        participantBarriersCreated: (state, action) => {
            const participantBarriers = state.currentParticipantBarriers.filter(
                (el) => !action.payload.some((entry) => entry.barrierId === el.id)
            );
            state.currentParticipantBarriers = [...participantBarriers, ...action.payload];
        },
        participantBarrierUpdated: (state, action) => {
            state.currentParticipantBarriers = state.currentParticipantBarriers.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        participantPastEducationUpdated: (state, action) => {
            state.currentParticipantPastEducation = action.payload;
        },

        participantWorkConditionalityUpdated: (state, action) => {
            state.currentParticipantWorkConditionality = action.payload;
        },

        participantSectionHeadingsCreated: (state, action) => {
            state.participantSectionHeadings = action.payload;
        },

        participantSectionHeadingsLoaded: (state, action) => {
            state.participantSectionHeadings = action.payload;
        },
        clearParticipantSectionHeadings: (state) => {
            state.participantSectionHeadings = {};
        },

        participantSectionHeadingsUpdated: (state, action) => {
            state.participantSectionHeadings = action.payload;
        },

        participantTimelineEventCreated: (state, action) => {
            state.participantTimelineEvent = action.payload;
            state.participantTimelineEvents = [...state.participantTimelineEvents, action.payload];
        },

        participantTimelineEventsLoaded: (state, action) => {
            state.loadingParticipantTimelineEvents = false;
            if (!('content' in action.payload)) {
                state.participantTimelineEvents = action.payload;
            } else {
                const { content, ...rest } = action.payload;
                state.participantTimelineEvents = rest.first
                    ? content
                    : [...state.participantTimelineEvents, ...content];
                state.participantTimelineEventsMetaData = rest;
            }
        },

        participantTimelineEventsLoading: (state) => {
            state.loadingParticipantTimelineEvents = true;
        },

        participantTimelineEventsLoadingFailure: (state) => {
            state.loadingParticipantTimelineEvents = false;
        },

        clearParticipantTimelineEvents: (state) => {
            state.participantTimelineEvents = [];
            state.participantTimelineEventsMetaData = {};
        },

        participantTimelineEventsSearchDataLoaded: (state, action) => {
            state.loadingParticipantTimelineEventsSearchData = false;
            if (!('content' in action.payload)) {
                state.participantTimelineEventsSearchData = action.payload;
            } else {
                const { content, ...rest } = action.payload;
                state.participantTimelineEventsSearchData = rest.first
                    ? content
                    : [...state.participantTimelineEventsSearchData, ...content];
                state.participantTimelineEventsSearchMetaData = rest;
            }
        },

        participantTimelineEventsSearchDataLoadingFailure: (state) => {
            state.loadingParticipantTimelineSearchData = false;
        },

        participantTimelineEventsSearchDataLoading: (state) => {
            state.loadingParticipantTimelineEventsSearchData = true;
        },

        clearTimelineEventsSearchData: (state) => {
            state.loadingParticipantTimelineEventsSearchData = false;
            state.participantTimelineEventsSearchData = [];
            state.participantTimelineEventsSearchMetaData = {};
        },

        participantAuditEventSearchDataLoading: (state) => {
            state.loadingParticipantAuditEventSearchData = true;
        },

        participantAuditEventSearchDataLoaded: (state, action) => {
            state.loadingParticipantAuditEventSearchData = false;
            state.participantAuditEventSearchData = action.payload;
        },

        participantAuditEventSearchDataLoadingFailure: (state) => {
            state.loadingParticipantAuditEventSearchData = false;
        },

        clearParticipantAuditEventSearchData: (state) => {
            state.participantAuditEventSearchData = [];
        },

        participantWorkConditionalityLoaded: (state, action) => {
            state.currentParticipantWorkConditionality =
                action.payload === '' ? {} : action.payload;
        },

        participantWorkHistoryLoading: (state) => {
            state.currentParticipantWorkHistory = {};
        },

        participantWorkHistoryLoaded: (state, action) => {
            state.currentParticipantWorkHistory = action.payload === '' ? {} : action.payload;
        },

        participantWorkHistoryUpdated: (state, action) => {
            state.currentParticipantWorkHistory = action.payload;
        },

        clearCurrentParticipantWorkHistory: (state) => {
            state.currentParticipantWorkHistory = {};
        },

        physicalActivitiesLoading: (state) => {
            state.physicalActivities = [];
            state.physicalActivitiesMetaData = { loaded: false };
        },
        physicalActivitiesLoaded: (state, action) => {
            state.physicalActivities = action.payload;
            state.physicalActivitiesMetaData = { loaded: true };
        },
        clearPhysicalActivities: (state) => {
            state.physicalActivities = [];
            state.physicalActivitiesMetaData = { loaded: false };
        },
        physicalActivityCreated: (state, action) => {
            state.physicalActivities = [...state.physicalActivities, action.payload];
        },
        physicalActivityUpdated: (state, action) => {
            state.physicalActivities = state.physicalActivities.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        physicalActivityDeleted: (state, action) => {
            state.physicalActivities = state.physicalActivities.filter(
                (el) => el.id !== action.payload.id
            );
        },

        postcodeServiceTeamMappingLoaded: (state, action) => {
            state.postcodeServiceTeamMapping = action.payload === '' ? {} : action.payload;
        },

        postcodeServiceTeamMappingLoading: (state) => {
            state.postcodeServiceTeamMapping = {};
        },

        questionnaireAnswerDeleted: (state, action) => {
            state.questionnaireAnswers = state.questionnaireAnswers.filter(
                (el) => el.id !== action.payload
            );
        },

        questionnaireAnswersLoading: (state) => {
            state.loadingQuestionnaireAnswers = true;
        },

        questionnaireAnswersLoadingFailure: (state) => {
            state.loadingQuestionnaireAnswers = false;
        },

        questionnaireAnswersLoaded: (state, action) => {
            state.loadingQuestionnaireAnswers = false;
            state.questionnaireAnswers = action.payload;
        },
        clearQuestionnaireAnswers: (state) => {
            state.questionnaireAnswers = [];
        },

        currentQuestionnaireAnswersLoaded: (state, action) => {
            state.currentQuestionnaireAnswers = action.payload;
        },
        clearCurrentQuestionnaireAnswers: (state) => {
            state.currentQuestionnaireAnswers = {};
        },

        questionnaireAnswersUpdated: (state, action) => {
            const questionnaireAnswers = state.questionnaireAnswers.filter(
                (el) => el.id !== action.payload.id
            );
            state.questionnaireAnswers = [...questionnaireAnswers, action.payload];

            if (!action.payload.draft) state.currentQuestionnaireAnswers = action.payload;
        },

        questionnairesLoading: (state) => {
            state.loadingQuestionnaires = true;
        },

        questionnairesLoadingFailure: (state) => {
            state.loadingQuestionnaires = false;
        },

        questionnairesLoaded: (state, action) => {
            state.loadingQuestionnaires = false;
            state.questionnaires = action.payload;
        },

        questionnaireLoaded: (state, action) => {
            state.currentQuestionnaire = action.payload;
        },

        questionnaireCreated: (state, action) => {
            state.currentQuestionnaire = action.payload;
            state.questionnaires = [...state.questionnaires, action.payload];
        },

        questionnaireUpdated: (state, action) => {
            const questionnaires = state.questionnaires.filter((el) => el.id !== action.payload.id);
            state.questionnaires = [...questionnaires, action.payload];
            state.currentQuestionnaire = action.payload;
        },

        setCurrentQuestionnaire: (state, action) => {
            state.currentQuestionnaire = action.payload;
        },

        questionnaireContractsLoading: (state) => {
            state.loadingQuestionnaireContracts = true;
            state.questionnaireContracts = {};
        },

        questionnaireContractsLoadingFailure: (state) => {
            state.loadingQuestionnaireContracts = false;
        },
        clearQuestionnaireContracts: (state) => {
            state.loadingQuestionnaireContracts = false;
            state.questionnaireContracts = {};
        },

        questionnaireContractsLoaded: (state, action) => {
            state.loadingQuestionnaireContracts = false;
            state.questionnaireContracts = action.payload;
        },

        questionnaireContractUpdated: (state, action) => {
            state.questionnaireContracts = action.payload;
        },

        serviceConstantsLoaded: (state, action) => {
            state.serviceConstants =
                action.payload === ''
                    ? state.serviceConstants
                    : {
                          ...state.serviceConstants,
                          [action.payload.serviceId]: action.payload
                      };
        },

        serviceConstantsUpdated: (state, action) => {
            state.serviceConstants = {
                ...state.serviceConstants,
                [action.payload.serviceId]: action.payload
            };
        },

        signPostingsLoading: (state) => {
            state.signPostings = [];
            state.signPostingsMetaData = { loaded: false };
        },

        signPostingsLoaded: (state, action) => {
            state.signPostings = action.payload;
            state.signPostingsMetaData = { loaded: true };
        },
        clearSignPostings: (state) => {
            state.signPostings = [];
            state.signPostingsMetaData = { loaded: false };
        },

        signPostingCreated: (state, action) => {
            state.signPostings = [...state.signPostings, action.payload];
        },

        signPostingUpdated: (state, action) => {
            state.signPostings = state.signPostings.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        signPostingDeleted: (state, action) => {
            state.signPostings = state.signPostings.filter((el) => el.id !== action.payload.id);
        },

        smokingQuitAttemptsLoading: (state) => {
            state.smokingQuitAttempts = [];
            state.smokingQuitAttemptReviews = {};
        },

        smokingQuitAttemptsLoaded: (state, action) => {
            state.smokingQuitAttempts = action.payload;
            state.smokingQuitAttemptReviews = action.payload?.reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur.id]: cur.smokingQuitAttemptReviews
                }),
                {}
            );
        },

        smokingQuitAttemptCreated: (state, action) => {
            state.smokingQuitAttempts = [...state.smokingQuitAttempts, action.payload];
            // no reviews yet at this point
        },

        smokingQuitAttemptUpdated: (state, action) => {
            state.smokingQuitAttempts = state.smokingQuitAttempts.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            // reviews are updated separately
        },

        smokingQuitAttemptReviewLoaded: (state, action) => {
            const id = action.payload?.smokingQuitAttemptId;
            if (!id) return;
            const smokingQuitAttemptReviews = {
                ...state.smokingQuitAttemptReviews,
                [id]:
                    state.smokingQuitAttemptReviews[id] &&
                    Array.isArray(state.smokingQuitAttemptReviews[id])
                        ? [
                              ...state.smokingQuitAttemptReviews[id].filter(
                                  (el) => el.id !== action.payload.id
                              ),
                              action.payload
                          ]
                        : [action.payload]
            };

            state.smokingQuitAttemptReviews = smokingQuitAttemptReviews;
            state.smokingQuitAttempts = state.smokingQuitAttempts.map((el) =>
                el.id === id
                    ? { ...el, smokingQuitAttemptReviews: smokingQuitAttemptReviews[el.id] }
                    : el
            );
        },

        smokingQuitAttemptReviewDeleted: (state, action) => {
            state.smokingQuitAttempts = state.smokingQuitAttempts.map((el) => ({
                ...el,
                smokingQuitAttemptReviews: el.smokingQuitAttemptReviews.map((entry) => ({
                    ...entry,
                    inactive: entry.id === action.payload.reviewId ? true : entry.inactive
                }))
            }));

            state.smokingQuitAttemptReviews = {
                ...state.smokingQuitAttemptReviews,
                [action.payload.quitAttemptId]: state.smokingQuitAttemptReviews[
                    action.payload.quitAttemptId
                ].map((el) => ({
                    ...el,
                    inactive: el.id === action.payload.reviewId ? true : el.inactive
                }))
            };
            // soft delete - inactive col. set to true
        },

        stepsLoading: (state) => {
            state.steps = [];
            state.stepsMetaData = { loaded: false };
        },

        stepsLoaded: (state, action) => {
            state.steps = action.payload;
            state.stepsMetaData = { loaded: true };
        },
        clearSteps: (state) => {
            state.steps = [];
            state.stepsMetaData = { loaded: false };
        },

        stepCreated: (state, action) => {
            state.steps = [...state.steps, action.payload];
        },

        stepUpdated: (state, action) => {
            state.steps = state.steps.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        stepDeleted: (state, action) => {
            state.steps = state.steps.filter((el) => el.id !== action.payload.id);
        },

        t2drHealthDataEntriesLoading: (state) => {
            state.t2drHealthDataEntries = [];
            state.t2drHealthDataEntriesMetaData = { loaded: false };
        },
        t2drHealthDataEntriesLoaded: (state, action) => {
            state.t2drHealthDataEntries = action.payload;
            state.t2drHealthDataEntriesMetaData = { loaded: true };
        },
        clearT2DRHealthDataEntries: (state) => {
            state.t2drHealthDataEntries = [];
            state.t2drHealthDataEntriesMetaData = { loaded: false };
        },
        t2drHealthDataEntryCreated: (state, action) => {
            state.t2drHealthDataEntries = [...state.t2drHealthDataEntries, action.payload];
        },
        t2drHealthDataEntryUpdated: (state, action) => {
            state.t2drHealthDataEntries = state.t2drHealthDataEntries.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        t2drPersonalInformationDataLoading: (state) => {
            state.loadingT2DRPersonalInformationData = true;
        },
        t2drPersonalInformationDataLoadingFailure: (state) => {
            state.loadingT2DRPersonalInformationData = false;
        },
        t2drPersonalInformationDataLoaded: (state, action) => {
            state.loadingT2DRPersonalInformationData = false;
            state.t2drPersonalInformationData = action.payload === '' ? {} : action.payload;
        },
        t2drPersonalInformationDataUpdated: (state, action) => {
            state.t2drPersonalInformationData = action.payload === '' ? {} : action.payload;
        },
        clearT2DRPersonalInformationData: (state) => {
            state.loadingT2DRPersonalInformationData = false;
            state.t2drPersonalInformationData = {};
        },

        waistMeasurementsLoading: (state) => {
            state.waistMeasurements = [];
            state.waistMeasurementsMetaData = { loaded: false };
        },

        waistMeasurementsLoaded: (state, action) => {
            state.waistMeasurements = action.payload;
            state.waistMeasurementsMetaData = { loaded: true };
        },
        clearWaistMeasurements: (state) => {
            state.waistMeasurements = [];
            state.waistMeasurementsMetaData = { loaded: false };
        },

        waistMeasurementCreated: (state, action) => {
            state.waistMeasurements = [...state.waistMeasurements, action.payload];
        },

        waistMeasurementUpdated: (state, action) => {
            state.waistMeasurements = state.waistMeasurements.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        waistMeasurementDeleted: (state, action) => {
            state.waistMeasurements = state.waistMeasurements.filter(
                (el) => el.id !== action.payload.id
            );
        },

        setParticipantSearchConfig: (state, action) => {
            const { type, searchTerm } = action.payload;
            state.participantSearchConfig = { type, searchTerm };
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        },

        errorOccurred: (state, action) => {
            state.errorMessage = action.payload;
        },

        businessLogicErrorOccurred: (state, action) => {
            state.businessLogicError = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },

        clearBusinessLogicError: (state) => {
            state.businessLogicError = {};
        },

        validationErrorOccurred: (state, action) => {
            state.validationErrors = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },
        clearValidationErrors: (state) => {
            state.validationErrors = {};
        }
    }
});

// ActionCreators. These are commands so 'createParticipant' (command) rather
// than 'participantCreated' (event that has happened)

export const loadParticipantAuditEvent = (type, id, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getParticipantURL() + `rest/${type}/audit/${id}/${encodedField}`,
        onStart: participantAuditEventSearchDataLoading.type,
        onSuccess: participantAuditEventSearchDataLoaded.type,
        successMessage: 'Audit entry has been searched',
        onError: participantAuditEventSearchDataLoadingFailure.type
    });
};

export const loadPrimaryDetailsAuditEvent = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'eventDate',
    dir = 'desc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant-timeline-event/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: participantTimelineEventsSearchDataLoading.type,
        onSuccess: participantTimelineEventsSearchDataLoaded.type,
        successMessage: 'Timeline entry has been searched',
        onError: participantTimelineEventsSearchDataLoadingFailure.type
    });

export const loadActionPlans = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/action-plan/participant/${participantId}`,
        onStart: actionPlansLoading.type,
        onSuccess: actionPlansLoaded.type,
        onError: actionPlansLoadingFailure.type
    });

export const createActionPlan = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/action-plan',
        method: 'post',
        data,
        onSuccess: actionPlanCreated.type,
        successMessage: 'Participant action plan has been added',
        onError: errorOccurred.type
    });

export const updateActionPlan = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/action-plan/${data.id}`,
        method: 'put',
        data,
        onSuccess: actionPlanUpdated.type,
        successMessage: 'Participant action plan has been updated',
        onError: errorOccurred.type
    });

export const loadAlcoholConsumed = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/alcohol-consumed/participant/${participantId}`,
        onStart: alcoholConsumedLoading.type,
        onSuccess: alcoholConsumedLoaded.type,
        successMessage: 'Alcohol consumed entries have been loaded',
        onError: errorOccurred.type
    });

export const createAlcoholConsumed = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/alcohol-consumed`,
        method: 'post',
        data,
        onSuccess: alcoholConsumedCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateAlcoholConsumed = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/alcohol-consumed/${id}`,
        method: 'put',
        data,
        onSuccess: alcoholConsumedUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteAlcoholConsumed = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/alcohol-consumed/${id}`,
        method: 'delete',
        onSuccess: alcoholConsumedDeleted.type,
        successMessage: 'Alcohol consumed entry deleted',
        onError: errorOccurred.type
    });

export const loadAppointmentSectionHeadings = (id, type = 'appointment') =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant-section-heading/${id}/${type}`,
        onSuccess: appointmentSectionHeadingsLoaded.type,
        onError: errorOccurred.type
    });

export const createAppointmentSectionHeadings = (
    data,
    msg = 'Appointment section headings have been updated'
) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant-section-heading',
        method: 'post',
        data,
        onSuccess: appointmentSectionHeadingsCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateAppointmentSectionHeadings = (
    data,
    msg = 'Appointment section headings have been updated'
) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant-section-heading/${data.id}`,
        method: 'put',
        data,
        onSuccess: appointmentSectionHeadingsUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createAssetRequest = (data) => {
    return apiCallBegan({
        url: getParticipantURL() + `rest/asset-request`,
        method: 'post',
        data,
        onSuccess: assetRequestCreated.type,
        successMessage: `Asset request created`,
        onError: errorOccurred.type
    });
};
export const loadAssetRequests = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/asset-request/participant/${participantId}`,
        onStart: assetRequestsLoading.type,
        onSuccess: assetRequestsLoaded.type,
        onError: assetRequestsLoadingFailure.type
    });

export const updateAssetRequest = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/asset-request/${data.id}`,
        method: 'put',
        data,
        onSuccess: assetRequestsUpdated.type,
        successMessage: 'Asset request updated',
        onError: errorOccurred.type
    });

export const updateAssetIssueRequest = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/asset-request/issue/${data.id}`,
        method: 'put',
        data,
        onSuccess: assetRequestsUpdated.type,
        successMessage: 'Asset issue request updated',
        onError: errorOccurred.type
    });

export const loadParticipantBarriers = (id) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/barrier/participant/' + id,
        onStart: participantBarriersLoading.type,
        onSuccess: participantBarriersLoaded.type,
        successMessage: 'Participant barriers loaded',
        onError: participantBarriersLoadingFailure.type
    });

export const addParticipantBarrier = (data, message) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/barrier',
        method: 'post',
        data: data,
        onSuccess: participantBarrierAdded.type,
        successMessage: message,
        onError: errorOccurred.type
    });

export const createParticipantBarriers = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/barrier/batch',
        method: 'post',
        data: data,
        onSuccess: participantBarriersCreated.type,
        successMessage: 'Participant barrier(s) created',
        onError: errorOccurred.type
    });

export const updateParticipantBarrier = (data, message = 'Barrier has been updated') =>
    apiCallBegan({
        url: getParticipantURL() + `rest/barrier/${data.id}`,
        method: 'put',
        data: data,
        onSuccess: participantBarrierUpdated.type,
        successMessage: message,
        onError: errorOccurred.type
    });

export const loadBloodGlucoseEntries = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-glucose/participant/${participantId}`,
        onStart: bloodGlucoseEntriesLoading.type,
        onSuccess: bloodGlucoseEntriesLoaded.type,
        successMessage: 'Blood glucose entries have been loaded',
        onError: errorOccurred.type
    });

export const createBloodGlucose = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-glucose`,
        method: 'post',
        data,
        onSuccess: bloodGlucoseCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateBloodGlucose = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-glucose/${id}`,
        method: 'put',
        data,
        onSuccess: bloodGlucoseUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteBloodGlucose = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-glucose/${id}`,
        method: 'delete',
        onSuccess: bloodGlucoseDeleted.type,
        successMessage: 'Blood glucose measurement deleted',
        onError: errorOccurred.type
    });

export const loadBloodPressureEntries = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-pressure/participant/${participantId}`,
        onStart: bloodPressureEntriesLoading.type,
        onSuccess: bloodPressureEntriesLoaded.type,
        successMessage: 'Blood pressure entries have been loaded',
        onError: errorOccurred.type
    });

export const createBloodPressure = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-pressure`,
        method: 'post',
        data,
        onSuccess: bloodPressureCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateBloodPressure = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-pressure/${id}`,
        method: 'put',
        data,
        onSuccess: bloodPressureUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteBloodPressure = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-pressure/${id}`,
        method: 'delete',
        onSuccess: bloodPressureDeleted.type,
        successMessage: 'Blood pressure measurement deleted',
        onError: errorOccurred.type
    });

export const loadBloodTests = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-test/participant/${participantId}`,
        onStart: bloodTestsLoading.type,
        onSuccess: bloodTestsLoaded.type,
        successMessage: 'Blood test entries have been loaded',
        onError: errorOccurred.type
    });

export const createBloodTest = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-test`,
        method: 'post',
        data,
        onSuccess: bloodTestCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateBloodTest = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-test/${id}`,
        method: 'put',
        data,
        onSuccess: bloodTestUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteBloodTest = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/blood-test/${id}`,
        method: 'delete',
        onSuccess: bloodTestDeleted.type,
        successMessage: 'Blood test deleted',
        onError: errorOccurred.type
    });

export const loadBMIEntries = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/bmi/participant/${participantId}`,
        onStart: bmiLoading.type,
        onSuccess: bmiLoaded.type,
        successMessage: 'BMI entries have been loaded',
        onError: errorOccurred.type
    });

export const createBMI = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/bmi`,
        method: 'post',
        data,
        onSuccess: bmiCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateBMI = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/bmi/${id}`,
        method: 'put',
        data,
        onSuccess: bmiUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteBMI = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/bmi/${id}`,
        method: 'delete',
        onSuccess: bmiDeleted.type,
        successMessage: 'BMI entry deleted',
        onError: errorOccurred.type
    });

export const loadBSOPDetails = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/bsop/${participantId}`,
        onStart: bsopDetailsLoading.type,
        onSuccess: bsopDetailsLoaded.type,
        successMessage: 'BSOP details have been loaded',
        onError: bsopDetailsLoadingFailure.type
    });

export const updateBSOPDetails = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/bsop/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: bsopDetailsUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantCircumstances = (id) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/circumstances/' + id,
        onSuccess: participantCircumstancesLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantCircumstances = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/circumstances/' + data.participantId,
        method: 'put',
        data,
        onSuccess: participantCircumstancesLoaded.type,
        successMessage: 'Participant circumstance details have been updated',
        onError: errorOccurred.type
    });

export const searchClaimEvents = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/claim-type/search`,
        method: 'post',
        data,
        onStart: claimEventSearchLoading.type,
        onSuccess: claimEventSearchLoaded.type,
        onError: errorOccurred.type
    });

export const updateClaimEvent = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/claim-type/${data.id}`,
        method: 'put',
        data,
        onStart: claimEventLoading.type,
        onSuccess: claimEventLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const searchClaimQueue = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'lastStatusUpdated',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/claim-queue/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: claimQueuesSearchLoading.type,
        onSuccess: claimQueuesSearchLoaded.type,
        onError: errorOccurred.type
    });

export const updateClaimQueue = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/claim-queue/${data.id}`,
        method: 'put',
        data,
        onStart: claimQueuesLoading.type,
        onSuccess: claimQueueUpdated.type,
        successMessage: `Claim queue has been updated`,
        onError: claimQueuesFailure.type
    });

export const updateClaimQueueActionStatus = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/claim-queue/action-status/${data.id}`,
        method: 'put',
        data,
        onSuccess: claimQueueUpdated.type,
        successMessage: msg,
        onError: claimQueuesFailure.type
    });

export const updateClaimQueueAssignee = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/claim-queue/assignee',
        method: 'put',
        data,
        onSuccess: claimQueuesUpdated.type,
        successMessage: `Claim queues have been updated`,
        onError: claimQueuesFailure.type
    });

export const updateClaimQueueProgress = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/claim-queue/progress',
        method: 'put',
        data,
        onSuccess: claimQueuesUpdated.type,
        successMessage: `Claim queues have been updated`,
        onError: claimQueuesFailure.type
    });

export const loadClaimQueueForecast = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/claim-queue/forecast/${participantId}`,
        onStart: claimQueueForecastLoading.type,
        onSuccess: claimQueueForecastLoaded.type,
        onError: errorOccurred.type
    });

export const updateClaimQueueStatus = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/claim-queue/resubmit/${data.id}`,
        method: 'put',
        data,
        onStart: claimQueuesLoading.type,
        onSuccess: claimQueueUpdated.type,
        successMessage: msg,
        onError: claimQueuesFailure.type
    });

export const createSelfEmploymentClaimQueueItem = (
    data,
    msg = 'Employment claim queue item created'
) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/claim-queue/self-employment`,
        method: 'post',
        data,
        onSuccess: claimQueueUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantConnectToWork = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `/rest/participant/connect-to-work/${participantId}`,
        onStart: participantConnectToWorkLoading.type,
        onSuccess: participantConnectToWorkLoaded.type,
        onError: participantConnectToWorkLoadingFailure.type
    });

export const updateConnectToWork = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `/rest/participant/connect-to-work/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: participantConnectToWorkUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadContractConstants = (contractId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/contract-constants/contract/${contractId}`,
        onStart: contractConstantsLoading.type,
        onSuccess: contractConstantsLoaded.type,
        onError: errorOccurred.type
    });

export const updateContractConstants = (data, message = 'Contract constants has been updated') =>
    apiCallBegan({
        url: getParticipantURL() + `rest/contract-constants/${data.id}`,
        method: 'put',
        data,
        onSuccess: contractConstantsUpdated.type,
        successMessage: message,
        onError: errorOccurred.type
    });

export const searchContractConstants = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/contract-constants/search',
        method: 'post',
        data,
        onSuccess: contractConstantsSearchLoaded.type,
        onError: errorOccurred.type
    });

export const createDar = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/dar',
        method: 'post',
        data,
        onSuccess: darCreated.type,
        successMessage: msg,
        onError: errorOccurred.type,
        onValidationError: validationErrorOccurred.type
    });

export const searchDarQueue = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/dar/search`,
        method: 'post',
        data,
        onStart: darQueuesSearchLoading.type,
        onSuccess: darQueuesSearchLoaded.type,
        onError: errorOccurred.type
    });

export const updateDar = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/dar/${data.id}`,
        method: 'put',
        data,
        onSuccess: darUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createDocmanTrigger = (data) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/contract-constants/contract/${data.contractId}/docman-triggers`,
        method: 'post',
        data,
        onSuccess: docmanTriggerCreated.type,
        successMessage: 'Docman trigger created',
        onError: errorOccurred.type
    });

export const loadDocmanTriggers = (contractId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/contract-constants/contract/${contractId}/docman-triggers`,
        onStart: docmanTriggersLoading.type,
        onSuccess: docmanTriggersLoaded.type,
        successMessage: 'Docman triggers loaded',
        onError: docmanTriggersLoadingFailure.type
    });

export const updateDocmanTrigger = (data) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/contract-constants/contract/${data.contractId}/docman-triggers`,
        method: 'put',
        data,
        onSuccess: docmanTriggerUpdated.type,
        successMessage: 'Docman trigger updated',
        onError: errorOccurred.type
    });

export const loadParticipantPastEducation = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/past-education/participant/${participantId}`,
        onSuccess: participantPastEducationLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantPastEducation = (data, participantId, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/past-education/participant/${participantId}`,
        method: 'put',
        data,
        onSuccess: participantPastEducationUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createEligibilityGroup = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group`,
        method: 'post',
        data,
        onSuccess: eligibilityGroupCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadEligibilityGroup = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/${id}`,
        onSuccess: eligibilityGroupLoaded.type,
        successMessage: `Eligibility group loaded`,
        onError: errorOccurred.type
    });

export const loadEligibilityGroups = () =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group?size=500`,
        onSuccess: eligibilityGroupsLoaded.type,
        successMessage: `Eligibility groups loaded`,
        onError: errorOccurred.type
    });

export const updateEligibilityGroup = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/${data.id}`,
        method: 'put',
        data,
        onSuccess: eligibilityGroupUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteEligibilityGroup = (msg, id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/${id}`,
        method: 'delete',
        onSuccess: eligibilityGroupUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const searchEligibilityGroups = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/eligibility-group/search`,
        method: 'post',
        data,
        onStart: eligibilityGroupSearchLoading.type,
        onSuccess: eligibilityGroupSearchLoaded.type,
        onError: errorOccurred.type
    });

export const loadParticipantEmployability = (id) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/employability/' + id,
        onStart: participantEmployabilityLoading.type,
        onSuccess: participantEmployabilityLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantEmployability = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/employability/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: participantEmployabilityLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createEPCRating = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/epc-rating',
        method: 'post',
        data,
        onSuccess: epcRatingCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantEPCRatings = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/epc-rating/participant/${participantId}`,
        onStart: epcRatingsLoading.type,
        onSuccess: epcRatingsLoaded.type,
        onError: epcRatingsLoadingFailure.type
    });

export const updateEPCRating = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/epc-rating/${data.id}`,
        method: 'put',
        data,
        onSuccess: epcRatingUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadFinancialRequests = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/financial-request/participant/${participantId}`,
        onStart: financialRequestsLoading.type,
        onSuccess: financialRequestsLoaded.type,
        onError: financialRequestsLoadingFailure.type
    });

export const createFinancialRequest = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/financial-request',
        method: 'post',
        data,
        onSuccess: financialRequestCreated.type,
        successMessage: 'Participant financial request has been added',
        onError: errorOccurred.type
    });

export const updateFinancialRequest = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/financial-request/${data.id}`,
        method: 'put',
        data,
        onSuccess: financialRequestUpdated.type,
        successMessage: 'Participant financial request has been updated',
        onError: errorOccurred.type
    });

export const updateFinancialIssueRequest = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/financial-request/issue/${data.id}`,
        method: 'put',
        data,
        onSuccess: financialRequestUpdated.type,
        successMessage: 'Participant financial issue request updated',
        onError: errorOccurred.type
    });

export const searchFinancialRequests = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'dateRequested',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/financial-request/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: financialRequestsSearchLoading.type,
        onSuccess: financialRequestsSearchLoaded.type,
        onError: errorOccurred.type
    });

export const createForm = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + 'rest/form',
        method: 'post',
        data,
        onSuccess: formCreated.type,
        successMessage: 'Form has been saved successfully',
        onError: errorOccurred.type
    });

export const loadForm = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/${id}`,
        onStart: formLoading.type,
        onSuccess: formLoaded.type,
        onError: errorOccurred.type
    });

export const loadForms = () =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form`,
        onStart: formsLoading.type,
        onSuccess: formsLoaded.type,
        onError: errorOccurred.type
    });

export const loadFormsByContract = (contractId) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/contract/${contractId}`,
        onSuccess: formsByContractLoaded.type,
        onError: errorOccurred.type
    });

export const updateForm = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/${data.id}`,
        method: 'put',
        data,
        onSuccess: formUpdated.type,
        successMessage: 'Form has been updated successfully',
        onError: errorOccurred.type
    });

export const loadFormContractByContract = (contractId) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/contract/${contractId}`,
        method: 'get',
        onSuccess: formContractsLoaded.type,
        onError: errorOccurred.type
    });

export const updateFormContract = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/contract/${data.contractId}`,
        method: 'put',
        data,
        onSuccess: formContractUpdated.type,
        successMessage: 'Form Contracts have been updated',
        onError: errorOccurred.type
    });

export const authoriseFormParticipant = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}/authorise`,
        method: 'put',
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been authorised successfully',
        onError: errorOccurred.type
    });

export const createFormParticipant = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${data.participantId}`,
        method: 'post',
        data,
        onSuccess: formParticipantCreated.type,
        successMessage: 'Form participant has been created successfully',
        onError: errorOccurred.type
    });

export const emailFormParticipant = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}/email`,
        method: 'put',
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been emailed successfully',
        onError: errorOccurred.type
    });

export const loadFormsParticipantByParticipantId = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}`,
        onSuccess: formsParticipantLoaded.type,
        onError: errorOccurred.type
    });

export const printFormParticipant = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}/print`,
        method: 'put',
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been printed successfully',
        onError: errorOccurred.type
    });

export const reopenFormParticipant = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}/re-open`,
        method: 'put',
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been reopened successfully',
        onError: errorOccurred.type
    });

export const searchFormParticipant = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/search`,
        method: 'post',
        data,
        onSuccess: formsParticipantLoaded.type,
        successMessage: 'Form participant has been loaded successfully',
        onError: errorOccurred.type
    });

export const updateFormParticipant = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${data.id}`,
        method: 'put',
        data,
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been updated successfully',
        onError: errorOccurred.type
    });

export const withdrawFormParticipant = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/form/participant/${id}/withdraw`,
        method: 'put',
        onSuccess: formParticipantUpdated.type,
        successMessage: 'Form participant has been withdrawn successfully',
        onError: errorOccurred.type
    });

export const loadParticipantGeneralInformation = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/general-information/${id}`,
        onSuccess: participantGeneralInformationLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantGeneralInformation = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/general-information/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: participantGeneralInformationUpdated.type,
        successMessage: 'Participant general details have been updated',
        onValidationError: validationErrorOccurred.type,
        onError: errorOccurred.type
    });

export const loadHealthIncidents = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/health-incident/participant/${participantId}`,
        onStart: healthIncidentsLoading.type,
        onSuccess: healthIncidentsLoaded.type,
        successMessage: 'Health Incident entries have been loaded',
        onError: errorOccurred.type
    });

export const createHealthIncident = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/health-incident`,
        method: 'post',
        data,
        onSuccess: healthIncidentCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateHealthIncident = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/health-incident/${id}`,
        method: 'put',
        data,
        onSuccess: healthIncidentUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteHealthIncident = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/health-incident/${id}`,
        method: 'delete',
        onSuccess: healthIncidentDeleted.type,
        successMessage: 'Health incident entry deleted',
        onError: errorOccurred.type
    });

export const loadHmrcData = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/hmrc-data/participant/${participantId}`,
        onStart: hmrcDataLoading.type,
        onSuccess: hmrcDataLoaded.type,
        successMessage: 'HMRC data has been loaded',
        onError: errorOccurred.type
    });

export const createIWSRecord = (data, businessName = '') =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/in-work-support/record',
        method: 'post',
        data,
        successMessage: `IWS Record ${businessName ? `for ${businessName} ` : ''}has been added`,
        onError: errorOccurred.type
    });

export const loadIWSRecordsByPlacementId = (
    placementId,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'frequencyWeekNo',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant/in-work-support/record/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data: { placementIds: [placementId] },
        onStart: iwsRecordsByPlacementIdLoading.type,
        onSuccess: iwsRecordsByPlacementIdLoaded.type,
        successMessage: `IWS Record entries for placement have been loaded`,
        onError: errorOccurred.type
    });

export const updateIWSRecord = (data, businessName = '') =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/in-work-support/record/${data.id}`,
        method: 'put',
        data,
        successMessage: `IWS Record ${businessName ? `for ${businessName} ` : ''}has been updated`,
        onError: errorOccurred.type
    });

export const updateIWSRecordAdhocCall = (data, businessName = '') =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/in-work-support/record/adhoc/${data.id}`,
        method: 'put',
        data,
        successMessage: `IWS Record ${businessName ? `for ${businessName} ` : ''}has been updated`,
        onError: errorOccurred.type
    });

export const searchIWSRecords = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'dueDate',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant/in-work-support/record/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: iwsRecordsLoading.type,
        onSuccess: iwsSearchRecordsLoaded.type,
        successMessage: `IWS Record entries for selected users have been loaded`,
        onError: errorOccurred.type
    });

export const loadNdppData = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/ndpp-data/${participantId}`,
        onSuccess: ndppDataLoaded.type,
        successMessage: `NDPP data for ${participantId} has been loaded`,
        onError: errorOccurred.type
    });

export const createNdppData = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/ndpp-data`,
        method: 'post',
        data,
        onSuccess: ndppDataCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateNdppData = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/ndpp-data/${data.id}`,
        method: 'put',
        data,
        onSuccess: ndppDataUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadOccActivity = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/occ/participant/${participantId}/activity`,
        onSuccess: occActivityLoaded.type,
        successMessage: `OCC activity for ${participantId} has been loaded`,
        onError: errorOccurred.type
    });

export const loadOccProfiles = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/occ/participant/${participantId}`,
        onSuccess: occProfilesLoaded.type,
        successMessage: 'OCC profile entries have been loaded',
        onError: errorOccurred.type
    });

export const createOccProfile = (participantId, data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/occ/participant/${participantId}`,
        method: 'post',
        data,
        onSuccess: occProfileCreated.type,
        successMessage: `OCC profile for ${participantId} has been created`,
        onError: errorOccurred.type
    });

export const loadParticipantHealthInformation = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/health-information/${id}`,
        onSuccess: participantHealthInformationLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantHealthInformation = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/health-information/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: participantHealthInformationLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantJobGoals = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-goal/participant/${id}`,
        onStart: participantJobGoalsLoading.type,
        onSuccess: participantJobGoalsLoaded.type,
        onError: participantJobGoalsLoadingFailure.type
    });

export const createParticipantJobGoal = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-goal`,
        method: 'post',
        data,
        successMessage: msg ? msg : 'Job goal created',
        onSuccess: participantJobGoalCreated.type,
        onError: errorOccurred.type
    });

export const updateParticipantJobGoal = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-goal/${data.id}`,
        method: 'put',
        data,
        successMessage: msg ? msg : 'Job goal updated',
        onSuccess: participantJobGoalUpdated.type,
        onError: errorOccurred.type
    });

export const deleteParticipantJobGoal = (id, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-goal/${id}`,
        method: 'delete',
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantJobReadiness = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-readiness/participant/${id}`,
        onStart: participantJobReadinessLoading.type,
        onSuccess: participantJobReadinessLoaded.type,
        onError: errorOccurred.type
    });

export const createParticipantJobReadiness = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/job-readiness',
        method: 'post',
        data,
        successMessage: 'Job Readiness created',
        onSuccess: participantJobReadinessCreated.type,
        onError: errorOccurred.type
    });

export const updateParticipantJobReadiness = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/job-readiness/${data.id}`,
        method: 'put',
        data,
        successMessage: 'Job Readiness Updated',
        onSuccess: participantJobReadinessUpdated.type,
        onError: errorOccurred.type
    });

export const loadParticipantJobSearch = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/supported-job-search/participant/${id}`,
        onStart: participantJobSearchLoading.type,
        onSuccess: participantJobSearchLoaded.type,
        onError: errorOccurred.type
    });

export const createParticipantJobSearch = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/supported-job-search',
        method: 'post',
        data,
        successMessage: 'Job Search created',
        onSuccess: participantJobSearchCreated.type,
        onError: errorOccurred.type
    });

export const updateParticipantJobSearch = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/supported-job-search/${data.id}`,
        method: 'put',
        data,
        successMessage: 'Job Search Updated',
        onSuccess: participantJobSearchUpdated.type,
        onError: errorOccurred.type
    });

export const deleteParticipantJobSearch = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/supported-job-search/${id}`,
        method: 'delete',
        successMessage: 'Job Search Deleted',
        onSuccess: participantJobSearchDeleted.type,
        onError: errorOccurred.type
    });

export const loadLTCPDetails = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/ltcp-data/participant/${participantId}`,
        onStart: ltcpDetailsLoading.type,
        onSuccess: ltcpDetailsLoaded.type,
        successMessage: 'LTCP details have been loaded',
        onError: ltcpDetailsLoadingFailure.type
    });

export const createLTCPDetails = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/ltcp-data`,
        method: 'post',
        data,
        onSuccess: ltcpDetailsCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateLTCPDetails = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/ltcp-data/${data.id}`,
        method: 'put',
        data,
        onSuccess: ltcpDetailsUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipant = (id) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/' + id,
        onStart: participantLoading.type,
        onSuccess: participantLoaded.type,
        onError: errorOccurred.type
    });

export const loadParticipantByPtCode = (ptCode) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/code/${ptCode}`,
        onStart: participantLoading.type,
        onSuccess: participantLoaded.type,
        onError: errorOccurred.type
    });

export const loadParticipants = () =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant?size=500',
        onSuccess: participantsLoaded.type,
        onError: errorOccurred.type
    });

// gets 10 most recent participants a user has accessed
export const loadRecentParticipants = () =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/simple-search-recent',
        onSuccess: recentParticipantsLoaded.type,
        onError: errorOccurred.type
    });

export const loadParticipantsSimpleSearch =
    (term = '', page = 0, size = DEFAULT_PAGE_LOAD_SIZE, sort = 'firstName', dir = 'asc') =>
    (dispatch) => {
        if (page === 0) dispatch(setParticipantSearchConfig({ type: 'simple', searchTerm: term }));
        dispatch(
            apiCallBegan({
                url:
                    getParticipantURL() +
                    `rest/participant/simple-search/${term}?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
                onStart: participantsSearchLoading.type,
                onSuccess: participantsSearchLoaded.type,
                onError: errorOccurred.type
            })
        );
    };

export const loadParticipantsAdvancedSearch =
    (data, page = 0, size = DEFAULT_PAGE_LOAD_SIZE, sort = 'firstName', dir = 'asc') =>
    (dispatch) => {
        if (page === 0) {
            dispatch(setParticipantSearchConfig({ type: 'advanced', searchTerm: data }));
        }
        dispatch(
            apiCallBegan({
                url:
                    getParticipantURL() +
                    `rest/participant/advanced-search?page=${page}&size=${size}&dir=${dir}${sort ? `&sort=${sort}` : ''}`,
                method: 'post',
                data,
                onStart: participantsSearchLoading.type,
                onSuccess: participantsSearchLoaded.type,
                onError: errorOccurred.type
            })
        );
    };

export const searchParticipants = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'firstName',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant/advanced-search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: participantsSearchLoading.type,
        onSuccess: participantsSearchLoaded.type,
        onError: errorOccurred.type
    });

export const searchParticipantsByIds = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'firstName',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant/advanced-search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data: data,
        onSuccess: participantsSearchByIdLoaded.type,
        onError: errorOccurred.type
    });

export const searchParticipantsByLoggedInUserServiceIds = (
    serviceIds,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'firstName',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant/advanced-search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data: { serviceIds },
        onStart: participantsByLoggedInUserServiceIdsLoading.type,
        onSuccess: participantsByLoggedInUserServiceIdsLoaded.type,
        onError: errorOccurred.type
    });

export const createParticipant = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant`,
        method: 'post',
        data,
        onSuccess: participantCreated.type,
        successMessage: msg,
        onError: errorOccurred.type,
        onValidationError: validationErrorOccurred.type,
        onBusinessLogicError: businessLogicErrorOccurred.type
    });

export const createParticipantContactCentre = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/contact-centre`,
        method: 'post',
        data,
        onSuccess: participantCreated.type,
        successMessage: msg,
        onError: errorOccurred.type,
        onValidationError: validationErrorOccurred.type,
        onBusinessLogicError: businessLogicErrorOccurred.type
    });

export const updateParticipant = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant/' + data.id,
        method: 'put',
        data,
        onSuccess: participantUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type,
        onValidationError: validationErrorOccurred.type,
        onBusinessLogicError: businessLogicErrorOccurred.type
    });

export const deleteParticipant = (id, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant/${id}`,
        method: 'delete',
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadPhysicalActivities = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/physical-activity/participant/${participantId}`,
        onStart: physicalActivitiesLoading.type,
        onSuccess: physicalActivitiesLoaded.type,
        successMessage: 'Physical activities have been loaded',
        onError: errorOccurred.type
    });

export const createPhysicalActivity = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/physical-activity`,
        method: 'post',
        data,
        onSuccess: physicalActivityCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updatePhysicalActivity = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/physical-activity/${id}`,
        method: 'put',
        data,
        onSuccess: physicalActivityUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deletePhysicalActivity = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/physical-activity/${id}`,
        method: 'delete',
        onSuccess: physicalActivityDeleted.type,
        successMessage: 'Physical activity deleted',
        onError: errorOccurred.type
    });

export const loadParticipantPrivacy = (contractId) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/contract-constants/contract/${contractId}/participant-privacy`,
        onSuccess: participantPrivacyLoaded.type,
        successMessage: `Participant Privacy loaded for contractId: ${contractId}`,
        onError: errorOccurred.type
    });

export const updateParticipantPrivacy = (data, msg) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/contract-constants/contract/${data.contractId}/participant-privacy`,
        method: 'put',
        data,
        onSuccess: participantPrivacyLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantSectionHeadings = (contractId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant-section-heading/${contractId}`,
        onSuccess: participantSectionHeadingsLoaded.type,
        onError: errorOccurred.type
    });

export const createParticipantSectionHeadings = (
    data,
    msg = 'Participant section headings have been updated'
) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant-section-heading',
        method: 'post',
        data,
        onSuccess: participantSectionHeadingsCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateParticipantSectionHeadings = (
    data,
    msg = 'Participant section headings have been updated'
) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant-section-heading/${data.id}`,
        method: 'put',
        data,
        onSuccess: participantSectionHeadingsUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createQuestionnaire = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + 'rest/questionnaire',
        method: 'post',
        data,
        onSuccess: questionnaireCreated.type,
        successMessage: 'Questionnaire has been created successfully',
        onError: errorOccurred.type
    });

export const deleteQuestionnaireAnswer = (id, msg) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/answer/${id}`,
        method: 'delete',
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadQuestionnaires = () =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire`,
        onStart: questionnairesLoading.type,
        onSuccess: questionnairesLoaded.type,
        onError: questionnairesLoadingFailure.type
    });

export const loadQuestionnaireById = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/${id}`,
        onSuccess: questionnaireLoaded.type,
        onError: errorOccurred.type
    });

export const updateQuestionnaire = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/${data.id}`,
        method: 'put',
        data,
        onSuccess: questionnaireUpdated.type,
        successMessage: 'Questionnaire has been updated successfully',
        onError: errorOccurred.type
    });

export const loadQuestionnaireAnswersById = (id) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/answer/${id}`,
        onSuccess: currentQuestionnaireAnswersLoaded.type,
        onError: errorOccurred.type
    });

export const loadQuestionnaireAnswersByParticipant = (participantId) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/answer/participant/${participantId}`,
        onStart: questionnaireAnswersLoading.type,
        onSuccess: questionnaireAnswersLoaded.type,
        onError: questionnaireAnswersLoadingFailure.type
    });

export const loadQuestionnaireAnswersByQuestionnaire = (questionnaireId) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/answer/questionnaire/${questionnaireId}`,
        onSuccess: questionnaireAnswersLoaded.type,
        onError: errorOccurred.type
    });

export const updateQuestionnaireAnswers = (data, msg) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + 'rest/questionnaire/answer',
        method: 'put',
        data,
        onSuccess: questionnaireAnswersUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createQuestionnaireContract = (data) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + 'rest/questionnaire/contract',
        method: 'post',
        data,
        onSuccess: questionnaireContractUpdated.type,
        successMessage: 'Questionnaire Contracts have been updated',
        onError: errorOccurred.type
    });

export const loadQuestionnaireContractByContract = (contractId) =>
    apiCallDirectusBegan({
        url: getParticipantURL() + `rest/questionnaire/contract/${contractId}`,
        method: 'get',
        onStart: questionnaireContractsLoading.type,
        onSuccess: questionnaireContractsLoaded.type,
        onError: questionnaireContractsLoadingFailure.type
    });

export const loadServiceConstants = (serviceId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/service-constants/service/${serviceId}`,
        onSuccess: serviceConstantsLoaded.type,
        onError: errorOccurred.type
    });

export const updateServiceConstants = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/service-constants/${data.id}`,
        method: 'put',
        data,
        onSuccess: serviceConstantsUpdated.type,
        successMessage: 'Service constants has been updated',
        onError: errorOccurred.type
    });

export const loadSignPostings = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/sign-posting/participant/${participantId}`,
        onStart: signPostingsLoading.type,
        onSuccess: signPostingsLoaded.type,
        successMessage: 'SignPostings have been loaded',
        onError: errorOccurred.type
    });

export const createSignPosting = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/sign-posting`,
        method: 'post',
        data,
        onSuccess: signPostingCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateSignPosting = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/sign-posting/${id}`,
        method: 'put',
        data,
        onSuccess: signPostingUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteSignPosting = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/sign-posting/${id}`,
        method: 'delete',
        onSuccess: signPostingDeleted.type,
        successMessage: 'Signposting deleted',
        onError: errorOccurred.type
    });

export const loadSmokingQuitAttempts = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/participant/${participantId}`,
        onStart: smokingQuitAttemptsLoading.type,
        onSuccess: smokingQuitAttemptsLoaded.type,
        successMessage: 'Smoking quit attempts have been loaded',
        onError: errorOccurred.type
    });

export const createSmokingQuitAttempt = (data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt`,
        method: 'post',
        data,
        onSuccess: smokingQuitAttemptCreated.type,
        successMessage: `Smoking quit attempt created`,
        onError: errorOccurred.type
    });

export const updateSmokingQuitAttempt = (id, data) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/${id}`,
        method: 'put',
        data,
        onSuccess: smokingQuitAttemptUpdated.type,
        successMessage: `Smoking quit attempt updated`,
        onError: errorOccurred.type
    });

export const deleteSmokingQuitAttempt = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/${id}`,
        method: 'delete',
        successMessage: `Smoking quit attempt deleted`,
        onError: errorOccurred.type
    });

export const createSmokingQuitAttemptReviews = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/${id}/reviews`,
        method: 'post',
        data,
        onSuccess: smokingQuitAttemptReviewsCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const createSmokingQuitAttemptReview = (smokingQuitAttemptId, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/${smokingQuitAttemptId}/review`,
        method: 'post',
        data,
        onSuccess: smokingQuitAttemptReviewLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateSmokingQuitAttemptReview = (smokingQuitAttemptId, data, msg) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/smoking-quit-attempt/${smokingQuitAttemptId}/review/${data.id}`,
        method: 'put',
        data,
        onSuccess: smokingQuitAttemptReviewLoaded.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteSmokingQuitAttemptReview = (id, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/smoking-quit-attempt/review/${id}`,
        method: 'delete',
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadSteps = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/step/participant/${participantId}`,
        onStart: stepsLoading.type,
        onSuccess: stepsLoaded.type,
        successMessage: 'Steps entries have been loaded',
        onError: errorOccurred.type
    });

export const createStep = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/step`,
        method: 'post',
        data,
        onSuccess: stepCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateStep = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/step/${id}`,
        method: 'put',
        data,
        onSuccess: stepUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteStep = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/step/${id}`,
        method: 'delete',
        onSuccess: stepDeleted.type,
        successMessage: 'Steps entry deleted',
        onError: errorOccurred.type
    });

export const loadT2DRHealthDataEntries = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/t2dr-health-data/participant/${participantId}`,
        onStart: t2drHealthDataEntriesLoading.type,
        onSuccess: t2drHealthDataEntriesLoaded.type,
        successMessage: 'T2DR health data entries have been loaded',
        onError: errorOccurred.type
    });

export const createT2DRHealthDataEntry = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/t2dr-health-data`,
        method: 'post',
        data,
        onSuccess: t2drHealthDataEntryCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateT2DRHealthDataEntry = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/t2dr-health-data/${id}`,
        method: 'put',
        data,
        onSuccess: t2drHealthDataEntryUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadT2DRPersonalInformationData = (participantId) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/t2dr-personal-information-data/participant/${participantId}`,
        onStart: t2drPersonalInformationDataLoading.type,
        onSuccess: t2drPersonalInformationDataLoaded.type,
        successMessage: 'Participant T2DR Personal Information Data has been loaded',
        onError: t2drPersonalInformationDataLoadingFailure.type
    });

export const updateT2DRPersonalInformationData = (data, msg) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/t2dr-personal-information-data/participant/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: t2drPersonalInformationDataUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantTimelineEvent = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'eventDate',
    dir = 'desc'
) =>
    apiCallBegan({
        url:
            getParticipantURL() +
            `rest/participant-timeline-event/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: participantTimelineEventsLoading.type,
        onSuccess: participantTimelineEventsLoaded.type,
        successMessage: 'TimelineEvents have been loaded',
        onError: participantTimelineEventsLoadingFailure.type
    });

export const createParticipantTimelineEvent = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant-timeline-event',
        method: 'post',
        data,
        onSuccess: participantTimelineEventCreated.type,
        successMessage: 'Timeline entry has been added',
        onError: errorOccurred.type
    });

export const deleteParticipantTimelineEvent = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/participant-timeline-event/${id}`,
        method: 'delete',
        successMessage: 'Timeline entry has been deleted',
        onError: errorOccurred.type
    });

export const createParticipantTimelineCommunicationEvent = (data) =>
    apiCallBegan({
        url: getParticipantURL() + 'rest/participant-timeline-communication-event',
        method: 'post',
        data,
        onSuccess: participantTimelineEventCreated.type,
        successMessage: 'Timeline communication entry has been added',
        onError: errorOccurred.type
    });

export const loadWaistMeasurements = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/waist-measurement/participant/${participantId}`,
        onStart: waistMeasurementsLoading.type,
        onSuccess: waistMeasurementsLoaded.type,
        successMessage: 'Waist measurement entries have been loaded',
        onError: errorOccurred.type
    });

export const createWaistMeasurement = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/waist-measurement`,
        method: 'post',
        data,
        onSuccess: waistMeasurementCreated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const updateWaistMeasurement = (id, data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/waist-measurement/${id}`,
        method: 'put',
        data,
        onSuccess: waistMeasurementUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const deleteWaistMeasurement = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/waist-measurement/${id}`,
        method: 'delete',
        onSuccess: waistMeasurementDeleted.type,
        successMessage: 'Waist measurement deleted',
        onError: errorOccurred.type
    });

export const loadParticipantWorkConditionality = (id) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/work-conditionality/participant/${id}`,
        onSuccess: participantWorkConditionalityLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantWorkConditionality = (data, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/work-conditionality/participant/${data.participantId}`,
        method: 'put',
        data,
        onSuccess: participantWorkConditionalityUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantWorkHistory = (participantId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/work-history/participant/${participantId}`,
        onStart: participantWorkHistoryLoading.type,
        onSuccess: participantWorkHistoryLoaded.type,
        onError: errorOccurred.type
    });

export const updateParticipantWorkHistory = (data, participantId, msg) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/work-history/participant/${participantId}`,
        method: 'put',
        data,
        onSuccess: participantWorkHistoryUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantServiceVersion = () =>
    apiVersion({
        url: getParticipantURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorOccurred.type
    });

export const loadPostcodeServiceTeamMapping = (postcode, contractId) =>
    apiCallBegan({
        url: getParticipantURL() + `rest/postcode-service-team/${postcode}/${contractId}`,
        method: 'get',
        onStart: postcodeServiceTeamMappingLoading.type,
        onSuccess: postcodeServiceTeamMappingLoaded.type,
        onError: errorOccurred.type
    });

export const setSuccessMessage = (message) => successMessageSet(message);
export const setErrorMessage = (message) => errorMessageSet(message);
export const setCurrentlySelectedParticipant = (participant) => participantUpdated(participant);
export const saveCurrentParticipantId = (currentParticipantId) => {
    localStorage.setItem('currentParticipantId', currentParticipantId);
};
export const onDeleteEligibilityGroup = (id) => eligibilityGroupDeleted(id);
export const onDeleteJobGoal = (id) => participantJobGoalDeleted(id);
export const onDeleteQuestionnaireAnswer = (id) => questionnaireAnswerDeleted(id);
export const onDeleteSmokingQuitAttemptReview = (quitAttemptId, reviewId) =>
    smokingQuitAttemptReviewDeleted({
        quitAttemptId,
        reviewId
    });

export const {
    clearAlcoholConsumed,
    clearAppointmentSectionHeadings,
    clearBloodGlucoseEntries,
    clearBloodPressureEntries,
    clearBloodTests,
    clearBmiEntries,
    clearBSOPDetails,
    clearClaimEvents,
    clearClaimQueues,
    clearContractConstantsSearch,
    clearCurrentParticipant,
    clearCurrentParticipantCircumstances,
    clearCurrentParticipantConnectToWork,
    clearCurrentQuestionnaireAnswers,
    clearDarEntry,
    clearDarQueue,
    clearDarQueueSearch,
    clearEligibilityGroupSearch,
    clearEPCRatings,
    clearFinancialRequestsSearch,
    clearFormContracts,
    clearFormsParticipant,
    clearHealthIncidents,
    clearIwsRecordsSearch,
    clearLTCPDetails,
    clearNdppData,
    clearOccActivity,
    clearOccProfiles,
    clearParticipantActionPlans,
    clearParticipantAssetRequests,
    clearParticipantBarriers,
    clearParticipantEmployability,
    clearParticipantFinancialRequests,
    clearParticipantGeneralInformation,
    clearParticipantJobGoals,
    clearParticipantJobReadiness,
    clearParticipantHealthInformation,
    clearParticipantSectionHeadings,
    clearParticipantsSearch,
    clearT2DRPersonalInformationData,
    clearPhysicalActivities,
    clearQuestionnaireAnswers,
    clearQuestionnaireContracts,
    clearParticipantTimelineEvents,
    clearSignPostings,
    clearSteps,
    clearT2DRHealthDataEntries,
    clearTimelineEventsSearchData,
    clearParticipantAuditEventSearchData,
    clearBusinessLogicError,
    clearValidationErrors,
    clearWaistMeasurements,
    setCurrentQuestionnaire,
    setCurrentForm
} = slice.actions;
// Put selectors for getting data from state here
// Selectors take state and return computed state

// Used internally
const {
    actionPlanCreated,
    actionPlansLoading,
    actionPlansLoadingFailure,
    actionPlansLoaded,
    actionPlanUpdated,
    alcoholConsumedLoading,
    alcoholConsumedLoaded,
    alcoholConsumedCreated,
    alcoholConsumedUpdated,
    alcoholConsumedDeleted,
    appointmentSectionHeadingsLoaded,
    appointmentSectionHeadingsCreated,
    appointmentSectionHeadingsUpdated,
    assetRequestCreated,
    assetRequestsLoading,
    assetRequestsLoadingFailure,
    assetRequestsLoaded,
    assetRequestsUpdated,
    bloodGlucoseEntriesLoading,
    bloodGlucoseEntriesLoaded,
    bloodGlucoseCreated,
    bloodGlucoseUpdated,
    bloodGlucoseDeleted,
    bloodPressureEntriesLoading,
    bloodPressureEntriesLoaded,
    bloodPressureCreated,
    bloodPressureUpdated,
    bloodPressureDeleted,
    bloodTestsLoading,
    bloodTestsLoaded,
    bloodTestCreated,
    bloodTestUpdated,
    bloodTestDeleted,
    bmiLoading,
    bmiLoaded,
    bmiCreated,
    bmiUpdated,
    bmiDeleted,
    bsopDetailsLoading,
    bsopDetailsLoaded,
    bsopDetailsLoadingFailure,
    bsopDetailsUpdated,
    claimEventLoading,
    claimEventLoaded,
    claimEventSearchLoading,
    claimEventSearchLoaded,
    claimQueueForecastLoading,
    claimQueueForecastLoaded,
    claimQueueUpdated,
    claimQueuesLoading,
    claimQueuesUpdated,
    claimQueuesFailure,
    claimQueuesSearchLoading,
    claimQueuesSearchLoaded,
    contractConstantsLoading,
    contractConstantsLoaded,
    contractConstantsUpdated,
    contractConstantsSearchLoaded,
    darCreated,
    darQueuesSearchLoading,
    darQueuesSearchLoaded,
    darUpdated,
    docmanTriggerCreated,
    docmanTriggersLoadingFailure,
    docmanTriggersLoaded,
    docmanTriggersLoading,
    docmanTriggerUpdated,
    errorMessageSet,
    errorOccurred,
    eligibilityGroupCreated,
    eligibilityGroupDeleted,
    eligibilityGroupLoaded,
    eligibilityGroupsLoaded,
    eligibilityGroupSearchLoading,
    eligibilityGroupSearchLoaded,
    eligibilityGroupUpdated,
    financialRequestsSearchLoading,
    financialRequestsSearchLoaded,
    epcRatingCreated,
    epcRatingsLoaded,
    epcRatingsLoading,
    epcRatingsLoadingFailure,
    epcRatingUpdated,
    financialRequestCreated,
    financialRequestsLoading,
    financialRequestsLoadingFailure,
    financialRequestsLoaded,
    financialRequestUpdated,
    formContractsLoaded,
    formContractUpdated,
    formCreated,
    formLoaded,
    formLoading,
    formUpdated,
    formsLoaded,
    formsByContractLoaded,
    formsLoading,
    formParticipantCreated,
    formsParticipantLoaded,
    formParticipantUpdated,
    healthIncidentCreated,
    healthIncidentDeleted,
    healthIncidentsLoaded,
    healthIncidentsLoading,
    healthIncidentUpdated,
    hmrcDataLoading,
    hmrcDataLoaded,
    iwsRecordsByPlacementIdLoading,
    iwsRecordsByPlacementIdLoaded,
    iwsRecordsLoading,
    iwsSearchRecordsLoaded,
    ltcpDetailsLoading,
    ltcpDetailsLoadingFailure,
    ltcpDetailsLoaded,
    ltcpDetailsCreated,
    ltcpDetailsUpdated,
    ndppDataLoaded,
    ndppDataCreated,
    ndppDataUpdated,
    occActivityLoaded,
    occProfileCreated,
    occProfilesLoaded,
    participantCreated,
    participantLoaded,
    participantLoading,
    participantUpdated,
    participantGeneralInformationUpdated,
    participantsLoaded,
    participantsByLoggedInUserServiceIdsLoading,
    participantsByLoggedInUserServiceIdsLoaded,
    recentParticipantsLoaded,
    participantBarriersLoading,
    participantBarriersLoadingFailure,
    participantBarriersLoaded,
    participantBarrierAdded,
    participantBarriersCreated,
    participantBarrierUpdated,
    participantCircumstancesLoaded,
    participantConnectToWorkLoaded,
    participantConnectToWorkLoading,
    participantConnectToWorkLoadingFailure,
    participantConnectToWorkUpdated,
    participantPastEducationLoaded,
    participantPastEducationUpdated,
    participantEmployabilityLoaded,
    participantEmployabilityLoading,
    participantGeneralInformationLoaded,
    participantHealthInformationLoaded,
    participantJobGoalCreated,
    participantJobGoalDeleted,
    participantJobGoalUpdated,
    participantJobGoalsLoading,
    participantJobGoalsLoadingFailure,
    participantJobGoalsLoaded,
    participantJobReadinessLoaded,
    participantJobReadinessLoading,
    participantJobReadinessCreated,
    participantJobReadinessUpdated,
    participantJobSearchLoaded,
    participantJobSearchLoading,
    participantJobSearchCreated,
    participantJobSearchUpdated,
    participantJobSearchDeleted,
    participantPrivacyLoaded,
    participantsSearchByIdLoaded,
    participantsSearchLoaded,
    participantsSearchLoading,
    participantSectionHeadingsLoaded,
    participantSectionHeadingsCreated,
    participantSectionHeadingsUpdated,
    participantTimelineEventCreated,
    participantTimelineEventsLoading,
    participantTimelineEventsLoadingFailure,
    participantTimelineEventsLoaded,
    participantTimelineEventsSearchDataLoading,
    participantTimelineEventsSearchDataLoadingFailure,
    participantTimelineEventsSearchDataLoaded,
    participantAuditEventSearchDataLoading,
    participantAuditEventSearchDataLoaded,
    participantAuditEventSearchDataLoadingFailure,
    participantWorkConditionalityLoaded,
    participantWorkHistoryLoaded,
    participantWorkHistoryLoading,
    participantWorkConditionalityUpdated,
    participantWorkHistoryUpdated,
    physicalActivitiesLoading,
    physicalActivitiesLoaded,
    physicalActivityCreated,
    physicalActivityUpdated,
    physicalActivityDeleted,
    postcodeServiceTeamMappingLoading,
    postcodeServiceTeamMappingLoaded,
    questionnaireAnswerDeleted,
    questionnaireAnswersLoading,
    questionnaireAnswersLoadingFailure,
    questionnaireAnswersLoaded,
    currentQuestionnaireAnswersLoaded,
    questionnaireAnswersUpdated,
    questionnaireContractUpdated,
    questionnaireContractsLoading,
    questionnaireContractsLoadingFailure,
    questionnaireContractsLoaded,
    questionnaireCreated,
    questionnaireLoaded,
    questionnaireUpdated,
    questionnairesLoading,
    questionnairesLoadingFailure,
    questionnairesLoaded,
    serviceConstantsLoaded,
    serviceConstantsUpdated,
    signPostingsLoading,
    signPostingsLoaded,
    signPostingCreated,
    signPostingUpdated,
    signPostingDeleted,
    smokingQuitAttemptsLoading,
    smokingQuitAttemptsLoaded,
    smokingQuitAttemptCreated,
    smokingQuitAttemptReviewsCreated,
    smokingQuitAttemptReviewLoaded,
    smokingQuitAttemptReviewDeleted,
    smokingQuitAttemptUpdated,
    stepsLoading,
    stepsLoaded,
    stepCreated,
    stepUpdated,
    stepDeleted,
    t2drHealthDataEntriesLoading,
    t2drHealthDataEntriesLoaded,
    t2drHealthDataEntryCreated,
    t2drHealthDataEntryUpdated,
    t2drPersonalInformationDataLoading,
    t2drPersonalInformationDataLoadingFailure,
    t2drPersonalInformationDataLoaded,
    t2drPersonalInformationDataUpdated,
    waistMeasurementsLoading,
    waistMeasurementsLoaded,
    waistMeasurementCreated,
    waistMeasurementUpdated,
    waistMeasurementDeleted,
    setParticipantSearchConfig,
    successMessageSet,
    validationErrorOccurred,
    businessLogicErrorOccurred,
    versionLoaded
} = slice.actions;

export default slice.reducer;
